import { useCallback, useEffect, useMemo } from "react";

import { atom, useAtom } from "jotai";

import {
  addDaysToIsoDate,
  getClosestUpcomingTuesday,
} from "@features/mmm/utils/get_cycle_weeks";
import { ARCHIVE_CYCLE } from "@gql/mmm-archive-cycle";
import {
  CREATE_BASIC_MEDIA_PLAN,
  FINALIZE_BASIC_MEDIA_PLAN,
  GET_BASIC_MEDIA_PLANS,
  UPDATE_BASIC_MEDIA_PLAN,
} from "@gql/mmm-media-plan";
import {
  useNorthbeamLazyQuery,
  useNorthbeamMutation,
  useNorthbeamQuery,
} from "@hooks/http";
import {
  type FetchUniversalMMMData,
  type ArchiveCycle,
  type ArchiveCycleVariables,
} from "@north-beam/nb-client-side";
import {
  type UpdateBasicMediaPlan,
  type UpdateBasicMediaPlanVariables,
  type CreateBasicMediaPlan,
  type CreateBasicMediaPlanVariables,
  type GetBasicMediaPlans,
  type FinalizeBasicMediaPlan,
  type FinalizeBasicMediaPlanVariables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import {
  type MMMClientState,
  type MMMClientCycleBase,
} from "@north-beam/nb-common";
import { MMM_GLOBAL_DATA_QUERY } from "@pages/mmm/explore-budgets-v2/explore-budgets-queries";
import { useClientCycles } from "@pages/mmm/hooks/use-client-cycles";

type hasDateRange = {
  date_range: {
    start_date: string;
    end_date: string;
  };
};

type hasEndDate = {
  end_date: string;
};
const refetchingMediaPlansAtom = atom<boolean>(false);

export function useMediaPlans() {
  const [isRefetchingMediaPlans, setIsRefetchingMediaPlans] = useAtom(
    refetchingMediaPlansAtom,
  );

  const {
    data: mediaPlansData,
    loading: loadingAllPlans,
    refetch: _refetchMediaPlans,
  } = useNorthbeamQuery<GetBasicMediaPlans>(GET_BASIC_MEDIA_PLANS);

  // NOTE: Consider refactoring to update apollo cache direclty
  // as done in use-delete-cycle.tsx. It would be slightly more complex
  // to add objects rather than evict them as in that case, but there
  // could be speed benefits
  const [_createBasicMediaPlan, { loading: loadingCreateMediaPlan }] =
    useNorthbeamMutation<CreateBasicMediaPlan, CreateBasicMediaPlanVariables>(
      CREATE_BASIC_MEDIA_PLAN,
      {
        doNotRaiseErrorOnFailure: true,
      },
    );

  const [_updateBasicMediaPlan, { loading: loadingUpdateMediaPlan }] =
    useNorthbeamMutation<UpdateBasicMediaPlan, UpdateBasicMediaPlanVariables>(
      UPDATE_BASIC_MEDIA_PLAN,
      {
        doNotRaiseErrorOnFailure: true,
      },
    );

  const [_deleteMediaPlan, { loading: isDeleteMediaPlanLoading }] =
    useNorthbeamMutation<ArchiveCycle, ArchiveCycleVariables>(ARCHIVE_CYCLE, {
      doNotRaiseErrorOnFailure: true,
    });

  const [_finalizeMediaPlan, { loading: isFinalizingMediaPlanLoading }] =
    useNorthbeamMutation<
      FinalizeBasicMediaPlan,
      FinalizeBasicMediaPlanVariables
    >(FINALIZE_BASIC_MEDIA_PLAN, {
      doNotRaiseErrorOnFailure: true,
    });

  const {
    loadData: loadMMMGlobalData,
    data: { data: mmmGlobalData },
  } = useNorthbeamLazyQuery<FetchUniversalMMMData>(MMM_GLOBAL_DATA_QUERY, {
    fetchPolicy: "network-only",
  });

  const [, setClientCycles] = useClientCycles();

  const updateMediaPlanEndDateForFrontend = <T extends hasDateRange>(
    mediaPlan: T,
  ) => {
    return {
      ...mediaPlan,
      date_range: {
        ...mediaPlan.date_range,
        end_date: addDaysToIsoDate(
          getClosestUpcomingTuesday(mediaPlan.date_range.end_date),
          -1,
        ),
      },
    };
  };

  const updateMediaPlanVariablesForBackend = <T extends hasEndDate>(
    mediaPlan: T,
  ) => {
    return {
      ...mediaPlan,
      end_date: getClosestUpcomingTuesday(mediaPlan.end_date),
    };
  };

  const mediaPlans = useMemo(
    () =>
      (mediaPlansData?.me.mmm.basicMediaPlansData.plans ?? []).map((plan) =>
        updateMediaPlanEndDateForFrontend(plan),
      ),
    [mediaPlansData],
  );

  const refetchMediaPlans = useCallback(async () => {
    setIsRefetchingMediaPlans(true);
    await _refetchMediaPlans();
    setIsRefetchingMediaPlans(false);
  }, [_refetchMediaPlans, setIsRefetchingMediaPlans]);

  const createMediaPlan = useCallback(
    async (variables: CreateBasicMediaPlanVariables) => {
      const res = await _createBasicMediaPlan({
        variables: updateMediaPlanVariablesForBackend(variables),
      });
      refetchMediaPlans();
      return res.data
        ? updateMediaPlanEndDateForFrontend(res.data.createBasicMediaPlanDraft)
        : null;
    },
    [_createBasicMediaPlan, refetchMediaPlans],
  );

  const updateMediaPlan = useCallback(
    async (variables: UpdateBasicMediaPlanVariables) => {
      const res = await _updateBasicMediaPlan({
        variables: updateMediaPlanVariablesForBackend(variables),
      });
      refetchMediaPlans();
      return res.data
        ? updateMediaPlanEndDateForFrontend(res.data.updateBasicMediaPlanDraft)
        : null;
    },
    [_updateBasicMediaPlan, refetchMediaPlans],
  );

  // NOTE: Prefer use-delete-cycle.tsx over this function
  const deleteMediaPlan = useCallback(
    async (cycleId: string) => {
      await _deleteMediaPlan({ variables: { cycleId } });
      refetchMediaPlans();
      loadMMMGlobalData();
    },
    [_deleteMediaPlan, refetchMediaPlans, loadMMMGlobalData],
  );

  useEffect(() => {
    if (mmmGlobalData?.me.mmm.cycles) {
      // TODO: Fix typing, figure out the best way
      // to fix that the state enums have diverged
      setClientCycles(
        mmmGlobalData.me.mmm.cycles.map((cycle) => ({
          ...cycle,
          state: cycle.state as unknown as MMMClientState,
        })) as MMMClientCycleBase[],
      );
    }
  }, [mmmGlobalData, setClientCycles]);

  const finalizeMediaPlan = useCallback(
    async (variables: FinalizeBasicMediaPlanVariables) => {
      const res = await _finalizeMediaPlan({ variables });
      refetchMediaPlans();
      loadMMMGlobalData();
      return res.data;
    },
    [_finalizeMediaPlan, refetchMediaPlans, loadMMMGlobalData],
  );

  const mediaPlanByIdLookup = useMemo(() => {
    return mediaPlans.reduce((acc, plan) => {
      acc[plan.id] = plan;
      return acc;
    }, {} as Record<string, GetBasicMediaPlans["me"]["mmm"]["basicMediaPlansData"]["plans"][number]>);
  }, [mediaPlans]);

  const availableConfigs =
    mediaPlansData?.me.mmm.basicMediaPlansData.modelConfigurations ?? [];

  const getMediaPlanById = (id: string) => mediaPlanByIdLookup[id] ?? null;

  const isLoading =
    loadingAllPlans || loadingCreateMediaPlan || loadingUpdateMediaPlan;

  return {
    mediaPlans,
    refetchMediaPlans,
    isRefetchingMediaPlans,
    availableConfigs,
    loadingAllPlans,
    getMediaPlanById,
    createMediaPlan,
    loadingCreateMediaPlan,
    updateMediaPlan,
    loadingUpdateMediaPlan,
    isLoading,
    deleteMediaPlan,
    isDeleteMediaPlanLoading,
    finalizeMediaPlan,
    isFinalizingMediaPlanLoading,
  };
}
